import React from 'react';
import { motion } from 'framer-motion';
import { TrendLine } from './TrendLine';
import type { AnalyticsSummary, SiteTimeSeries } from '../types';
import { ANIMATION_CONFIG } from '../constants';

interface KpiCardsProps {
    summary?: AnalyticsSummary;
    timeSeries: SiteTimeSeries[];
    isLoading: boolean;
}

interface KpiCardProps {
    label: string;
    value: string | number;
    suffix?: string;
    trend?: 'up' | 'down' | 'neutral';
    trendValue?: string;
    sparkline?: number[];
    color: 'brand' | 'success' | 'warning' | 'danger';
    delay: number;
}
 
const COLOR_MAP: Record<KpiCardProps['color'], string> = {
    brand:   'text-[var(--brand)]',
    success: 'text-[var(--status-up)]',
    warning: 'text-[var(--status-warn)]',
    danger:  'text-[var(--status-down)]',
};
 
const TREND_COLOR: Record<'up' | 'down' | 'neutral', string> = {
    up:      'text-[var(--status-up)]',
    down:    'text-[var(--status-down)]',
    neutral: 'text-[var(--text-muted)]',
};
 
const TREND_ICON: Record<'up' | 'down' | 'neutral', string> = {
    up:      '▲',
    down:    '▼',
    neutral: '—',
};

const KpiCard: React.FC<KpiCardProps> = ({
    label, value, suffix, trend, trendValue, sparkline, color, delay,
}) => (
    <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
            delay,
            duration: ANIMATION_CONFIG.duration,
            ease: ANIMATION_CONFIG.ease as [number, number, number, number],
        }}
        whileHover={{ y: -2, boxShadow: 'var(--shadow)' }}
        className="bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl p-3.5 transition-all hover:border-[var(--border-strong)]"
    >
        <div className="text-[10px] font-medium text-[var(--text-muted)] uppercase tracking-[0.06em] mb-2">
            {label}
        </div>
        
        <div className="flex items-end justify-between gap-2">
            <div>
                <div className={`font-mono text-[22px] font-medium tracking-[-0.5px] leading-none ${COLOR_MAP[color]}`}>
                    {value}
                    {suffix && <span className="text-[14px] text-[var(--text-muted)] ml-1">{suffix}</span>}
                </div>
                
                {trend && (
                    <div className={`text-[11px] mt-1.5 flex items-center gap-1 ${TREND_COLOR[trend]}`}>
                        <span>{TREND_ICON[trend]}</span>
                        <span>{trendValue}</span>
                    </div>
                )}
            </div>
            
            {sparkline && sparkline.length > 0 && (
                <div className="w-16 h-8">
                    <TrendLine data={sparkline} color={color} />
                </div>
            )}
        </div>
    </motion.div>
);

export const KpiCards: React.FC<KpiCardsProps> = ({ summary, timeSeries, isLoading }) => {
    if (isLoading || !summary) {
        return (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 px-[18px] pb-3.5">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-[88px] bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl animate-pulse" />
                ))}
            </div>
        );
    }

    const avgSparkline = timeSeries[0]?.data.map(d => d.uptime) ?? [];
    const responseSparkline = timeSeries[0]?.data.map(d => d.responseTime) ?? [];

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 px-[18px] pb-3.5">
            <KpiCard
                label="Общий Uptime"
                value={summary.overallUptime.toFixed(2)}
                suffix="%"
                trend="up"
                trendValue="+0.3% к прошлому периоду"
                sparkline={avgSparkline}
                color={summary.overallUptime >= 99.9 ? 'success' : summary.overallUptime >= 99 ? 'brand' : 'warning'}
                delay={0}
            />
            
            <KpiCard
                label="Среднее время ответа"
                value={summary.avgResponseTime}
                suffix="мс"
                trend="down"
                trendValue="−18 мс быстрее"
                sparkline={responseSparkline}
                color={summary.avgResponseTime < 300 ? 'success' : summary.avgResponseTime < 800 ? 'brand' : 'warning'}
                delay={0.05}
            />
            
            <KpiCard
                label="Всего инцидентов"
                value={summary.totalIncidents}
                suffix="шт"
                trend={summary.totalIncidents === 0 ? 'up' : 'down'}
                trendValue={summary.totalIncidents === 0 ? 'Всё стабильно' : '−2 к прошлому периоду'}
                color={summary.totalIncidents === 0 ? 'success' : summary.totalIncidents < 5 ? 'brand' : 'warning'}
                delay={0.1}
            />
            
            <KpiCard
                label="Сайтов на мониторинге"
                value={summary.sitesMonitored}
                suffix=""
                color="brand"
                delay={0.15}
            />
        </div>
    );
};